import { type ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Database, Info, Layers, Plug, Trophy } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { Badge } from "../../components/Badge/Badge";
import { Button } from "../../components/Button/Button";
import { Card, CardBody, CardHeader } from "../../components/Card/Card";
import { PageHeader } from "../../components/PageHeader/PageHeader";
import { SegmentedToggle } from "../../components/SegmentedToggle/SegmentedToggle";
import { useToast } from "../../components/Toast/toastContext";
import { initialsFor } from "../../components/Avatar/avatarLook";
import { ApiError, describeError } from "../../lib/errors";
import { useAuth } from "../auth/useAuth";
import { ForbiddenPage } from "../errors/ForbiddenPage";

import {
  getMcpStatus,
  listMcpClients,
  type McpClient,
  mcpClientsQueryKey,
  mcpStatusQueryKey,
  type McpStatus,
  type SourceId,
  type SourceStatus,
} from "./api";
import { EnvironmentBadge } from "./EnvironmentBadge";
import {
  clientInitial,
  clientMeta,
  formatAuthorizedAt,
  formatCount,
  formatLastUsed,
  formatTimeout,
  isDesktopClient,
} from "./format";
import { RevokeClientModal, type RevokeTarget } from "./RevokeClientModal";
import { buildSnippet, SNIPPET_OPTIONS, type SnippetKind } from "./snippets";
import styles from "./McpAccessPage.module.css";

const SUBTITLE =
  "Read-only access to this environment's Postgres and Redis for AI agents";

export function McpAccessPage() {
  const { user } = useAuth();
  if (!user) return null;
  // The API refuses a member anyway; answering here keeps the URL in the
  // 403 page's "share this URL" line.
  if (user.role !== "super_admin") return <ForbiddenPage />;
  return <McpAccessContent />;
}

function McpAccessContent() {
  const status = useQuery({
    queryKey: mcpStatusQueryKey,
    queryFn: getMcpStatus,
  });
  const data = status.data;

  return (
    <>
      <PageHeader
        title={
          <span className={styles.title}>
            MCP access
            {data && <EnvironmentBadge environment={data.environment} />}
          </span>
        }
        subtitle={SUBTITLE}
      />
      <div className={styles.body}>
        {status.isLoading ? (
          <div className={styles.loading}>Loading…</div>
        ) : status.isError || !data ? (
          <Alert variant="danger" title="Couldn't load MCP access">
            {status.error instanceof ApiError
              ? describeError(status.error.code)
              : describeError("internal")}
          </Alert>
        ) : data.enabled ? (
          <EnabledView status={data} />
        ) : (
          <DisabledView />
        )}
      </div>
    </>
  );
}

function EnabledView({ status }: { status: McpStatus }) {
  return (
    <>
      <div className={styles.grid2}>
        <ConnectCard status={status} />
        <DataSourcesCard status={status} />
      </div>
      <ClientsCard environment={status.environment} />
    </>
  );
}

function useCopy() {
  const { push } = useToast();
  return async (value: string, what: string) => {
    try {
      await navigator.clipboard.writeText(value);
      push({ variant: "success", message: `${what} copied to clipboard.` });
    } catch {
      push({
        variant: "danger",
        message: "Couldn't copy — clipboard unavailable.",
      });
    }
  };
}

function ConnectCard({ status }: { status: McpStatus }) {
  const [kind, setKind] = useState<SnippetKind>("claude-code");
  const copy = useCopy();
  const snippet = buildSnippet(kind, status.server_name, status.server_url);

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className={styles.cardTitle}>Connect an agent</h3>
          <div className={styles.cardSub}>
            Super admins sign in with their Social Hub account on first use.
          </div>
        </div>
        <Badge variant="success" dot>
          Enabled
        </Badge>
      </CardHeader>
      <CardBody>
        <div className={styles.credRow}>
          <span className={styles.credKey}>Server URL</span>
          <span className={styles.credValue}>{status.server_url}</span>
          <Button
            variant="secondary"
            size="sm"
            aria-label="Copy server URL"
            onClick={() => void copy(status.server_url, "Server URL")}
          >
            Copy
          </Button>
        </div>
        <div className={styles.seg}>
          <SegmentedToggle
            aria-label="Client"
            options={SNIPPET_OPTIONS}
            value={kind}
            onChange={setKind}
          />
        </div>
        <div className={styles.snippetWrap}>
          <pre className={styles.snippet} data-testid="mcp-snippet">
            {snippet.node}
          </pre>
          <button
            type="button"
            className={styles.copy}
            aria-label="Copy snippet"
            onClick={() => void copy(snippet.copy, "Snippet")}
          >
            Copy
          </button>
        </div>
        <div className={styles.hint}>
          <Info size={14} className={styles.hintIcon} aria-hidden="true" />
          Your browser opens this admin to sign in and approve the connection.
          No keys to copy.
        </div>
      </CardBody>
    </Card>
  );
}

const SOURCES: Record<
  SourceId,
  { name: string; icon: ReactNode; meta: ReactNode }
> = {
  postgres: {
    name: "Postgres",
    icon: <Database size={16} aria-hidden="true" />,
    meta: (
      <>
        Role <code>social_hub_mcp</code> · SELECT only · read-only transactions
      </>
    ),
  },
  redis: {
    name: "Redis",
    icon: <Layers size={16} aria-hidden="true" />,
    meta: (
      <>
        ACL user <code>mcp</code> · read commands · presence, rate limits,
        config cache
      </>
    ),
  },
  redis_cache: {
    name: "Redis cache",
    icon: <Trophy size={16} aria-hidden="true" />,
    meta: (
      <>
        ACL user <code>mcp</code> · read commands · leaderboard ZSETs
      </>
    ),
  },
};

const SOURCE_ORDER: SourceId[] = ["postgres", "redis", "redis_cache"];

function SourceBadge({ status }: { status: SourceStatus }) {
  if (status === "reachable") {
    return (
      <Badge variant="success" dot>
        Reachable
      </Badge>
    );
  }
  if (status === "unreachable") {
    return (
      <Badge variant="warning" dot>
        Unreachable
      </Badge>
    );
  }
  return <Badge variant="neutral">Not configured</Badge>;
}

function DataSourcesCard({ status }: { status: McpStatus }) {
  const byId = new Map(status.sources.map((s) => [s.id, s.status]));
  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className={styles.cardTitle}>Data sources</h3>
          <div className={styles.cardSub}>
            Set on the server. Change them in the environment&apos;s deploy
            variables.
          </div>
        </div>
      </CardHeader>
      <CardBody>
        <div className={styles.src}>
          {SOURCE_ORDER.map((id) => {
            const src = SOURCES[id];
            return (
              <div
                key={id}
                className={styles.srcRow}
                data-testid={`mcp-source-${id}`}
              >
                <span className={styles.srcIcon}>{src.icon}</span>
                <div>
                  <div className={styles.srcName}>{src.name}</div>
                  <div className={styles.srcMeta}>{src.meta}</div>
                </div>
                <SourceBadge status={byId.get(id) ?? "not_configured"} />
              </div>
            );
          })}
        </div>
        <div className={styles.limits}>
          <div>
            <div className={styles.limitKey}>Statement timeout</div>
            <div className={styles.limitValue}>
              {formatTimeout(status.statement_timeout_ms)}
            </div>
          </div>
          <div>
            <div className={styles.limitKey}>Rows per result</div>
            <div className={styles.limitValue}>
              {formatCount(status.max_rows)}
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function ClientsCard({ environment }: { environment: string }) {
  const { user: me } = useAuth();
  const [revoke, setRevoke] = useState<RevokeTarget | null>(null);
  const clientsQ = useQuery({
    queryKey: mcpClientsQueryKey,
    queryFn: listMcpClients,
  });
  const clients = clientsQ.data ?? [];

  return (
    <Card>
      <CardHeader>
        <div>
          <h3 className={styles.cardTitle}>
            Connected clients
            {clientsQ.data && <Badge variant="neutral">{clients.length}</Badge>}
          </h3>
          <div className={styles.cardSub}>
            Every super admin&apos;s authorized agents on this environment.
          </div>
        </div>
        {clients.length > 0 && (
          <Button
            variant="danger-outline"
            size="sm"
            onClick={() => setRevoke({ kind: "all", count: clients.length })}
          >
            Revoke all
          </Button>
        )}
      </CardHeader>
      {clientsQ.isLoading ? (
        <div className={styles.tableEmpty}>Loading…</div>
      ) : clientsQ.isError ? (
        <div className={styles.tableEmpty}>
          {clientsQ.error instanceof ApiError
            ? describeError(clientsQ.error.code)
            : describeError("internal")}
        </div>
      ) : clients.length === 0 ? (
        <div className={styles.tableEmpty}>No agents connected yet.</div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Client</th>
              <th>Authorized by</th>
              <th>Authorized</th>
              <th>Last used</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <ClientRow
                key={c.id}
                client={c}
                isMine={c.user.id === me?.id}
                onRevoke={() => setRevoke({ kind: "one", client: c })}
              />
            ))}
          </tbody>
        </table>
      )}
      <RevokeClientModal
        target={revoke}
        environment={environment}
        onClose={() => setRevoke(null)}
      />
    </Card>
  );
}

function ClientRow({
  client,
  isMine,
  onRevoke,
}: {
  client: McpClient;
  isMine: boolean;
  onRevoke: () => void;
}) {
  const meta = clientMeta(client.client_host, client.redirect_host);
  return (
    <tr>
      <td>
        <div className={styles.client}>
          <span
            className={
              isDesktopClient(client.client_name)
                ? `${styles.clientLogo} ${styles.clientLogoDesktop}`
                : styles.clientLogo
            }
            aria-hidden="true"
          >
            {clientInitial(client.client_name)}
          </span>
          <div>
            <div className={styles.clientName}>{client.client_name}</div>
            {meta && <div className={styles.clientMeta}>{meta}</div>}
          </div>
        </div>
      </td>
      <td>
        <span className={styles.person}>
          <span
            className={isMine ? styles.av : `${styles.av} ${styles.avOther}`}
            aria-hidden="true"
          >
            {initialsFor(client.user.name)}
          </span>
          {client.user.name}
        </span>
      </td>
      <td className={styles.muted}>{formatAuthorizedAt(client.authorized_at)}</td>
      <td className={styles.muted}>{formatLastUsed(client.last_used_at)}</td>
      <td className={styles.right}>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`Revoke ${client.client_name} of ${client.user.name}`}
          onClick={onRevoke}
        >
          Revoke
        </Button>
      </td>
    </tr>
  );
}

function DisabledView() {
  return (
    <div className={styles.empty}>
      <div className={styles.emptyIcon}>
        <Plug size={24} aria-hidden="true" />
      </div>
      <div className={styles.emptyTitle}>MCP is off on this environment</div>
      <div className={styles.emptyDesc}>
        The server has no read-only database credentials, so agents can&apos;t
        connect. Set these deploy variables and redeploy:
      </div>
      <div className={styles.envlist}>
        SH_MCP_PG_URL{" "}
        <span className={styles.envlistComment}># read-only Postgres role</span>
        <br />
        SH_MCP_REDIS_URL{" "}
        <span className={styles.envlistComment}>
          # Redis ACL user, read commands
        </span>
        <br />
        SH_MCP_REDIS_CACHE_URL{" "}
        <span className={styles.envlistComment}># same, leaderboard cache</span>
      </div>
    </div>
  );
}
